// frontend/src/features/ups/UpsReservedDriverList.js
// This React component renders the UPS Reserved drivers list, honoring global filters,
// live-syncing via Socket.IO, and letting users activate drivers or—if admin—ban them via a reason modal.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye, Ban as BannedIcon } from 'lucide-react';
import socket from '../../services/socket';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import {
  makeSelectDriversForTab,
  upsertDriver,
  removeDriver,
  applyStatusChange,
} from '../drivers/driverSlice';
import { selectFilters } from '../ui/filtersSlice';

import { activateDriver, banDriver } from '../../services/upsDriverService';
import DriverDetailsModal from './modals/DriverDetailsModal';

const normalizeId = v => (v == null ? undefined : Number(v));

export default function UpsReservedDriverList() {
  const dispatch        = useDispatch();
  const isAdmin         = useSelector(selectIsUpsAdmin);
  const agencies        = useSelector(selectAllAgencies);
  const { agencyId, q } = useSelector(selectFilters);

  const selectReserved = useMemo(() => makeSelectDriversForTab('Reserved'), []);
  const drivers        = useSelector(selectReserved);

  // Focus-on-arrival via ?focus=<id>
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2','ring-blue-400','animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2','ring-blue-400','animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  // Realtime socket wiring (patch-only for lean events)
  useEffect(() => {
    const handleUpsert   = d => dispatch(upsertDriver({ ...d, id: normalizeId(d?.id) }));
    const handleDeleted  = ({ id }) => dispatch(removeDriver(normalizeId(id)));
    const handleBanned   = p => {
      const id = normalizeId(p?.id ?? p?.driver_id);
      if (id) dispatch(applyStatusChange({ id, status: 'Banned' }));
    };
    const handleUnbanned = p => {
      const id = normalizeId(p?.id ?? p?.driver_id);
      if (id) dispatch(applyStatusChange({ id, status: p?.status ?? 'Reserved' }));
    };
    const handleStatusChanged = ({ id, status }) => {
      const nId = normalizeId(id);
      if (nId) dispatch(applyStatusChange({ id: nId, status }));
    };

    socket.on('driverAdded', handleUpsert);
    socket.on('driverUpdated', handleUpsert);
    socket.on('driverDeleted', handleDeleted);
    socket.on('driverBanned', handleBanned);
    socket.on('driverUnbanned', handleUnbanned);
    socket.on('driver:statusChanged', handleStatusChanged);

    return () => {
      socket.off('driverAdded', handleUpsert);
      socket.off('driverUpdated', handleUpsert);
      socket.off('driverDeleted', handleDeleted);
      socket.off('driverBanned', handleBanned);
      socket.off('driverUnbanned', handleUnbanned);
      socket.off('driver:statusChanged', handleStatusChanged);
    };
  }, [dispatch]);

  const handleActivate = async id => {
    try {
      const updated = await activateDriver(id);
      dispatch(upsertDriver(updated));
    } catch (err) {
      console.error('Activate failed:', err);
    }
  };

  // Ban modal (same UX as Organized list)
  const [showBanModal, setShowBanModal]     = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [banReason, setBanReason]           = useState('');

  const openBanModal = driver => {
    if (!isAdmin) return;
    setSelectedDriver(driver);
    setBanReason('');
    setShowBanModal(true);
  };

  const handleBanConfirm = async () => {
    if (!banReason.trim() || !isAdmin) return;
    try {
      const banned = await banDriver(selectedDriver.id, banReason);
      dispatch(upsertDriver(banned)); // full DTO from API (safe)
      setShowBanModal(false);
      setSelectedDriver(null);
    } catch (err) {
      console.error('Ban failed:', err);
    }
  };

  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsDriver, setDetailsDriver] = useState(null);
  const openDetails = (driver) => { setDetailsDriver(driver); setShowDetails(true); };
  const closeDetails = () => { setShowDetails(false); setDetailsDriver(null); };

  // Empty-state labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      x => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();
  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <div className="overflow-x-auto my-6">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Reserved Drivers</h3>
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Name','Vehicle Reg.','Phone','Email','Agency','Submitted','Actions'].map(col => (
                <th key={col} className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {drivers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No reserved drivers{agencyId ? ` for ${scopeLabel}` : ''}{hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : drivers.map(driver => (
              <tr key={driver.id} data-row-id={driver.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{driver.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.vehicle_registration_number || driver.vrn || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.phone}</td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.agency_name || driver.agency?.name || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.submission_date ? new Date(driver.submission_date).toLocaleString() : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-4">
                    {/* Details first */}
                    <button
                      onClick={() => openDetails(driver)}
                      aria-label="View driver details"
                      title="View details"
                      className="p-1 hover:bg-gray-100 rounded text-blue-600"
                    >
                      <Eye size={16} />
                    </button>

                    {/* Activate */}
                    <button
                      onClick={() => handleActivate(driver.id)}
                      aria-label="Activate driver"
                      title="Activate"
                      className="p-1 hover:bg-gray-100 rounded text-green-600"
                    >
                      <UserCheck size={16} />
                    </button>

                    {/* Ban (admins only) */}
                    <button
                      onClick={() => openBanModal(driver)}
                      aria-label="Ban driver"
                      title={isAdmin ? 'Ban' : 'Admins only'}
                      disabled={!isAdmin}
                      className={
                        'p-1 rounded ' +
                        (isAdmin ? 'hover:bg-gray-100 text-red-600'
                                 : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                      }
                    >
                      <BannedIcon size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ban Modal */}
      {showBanModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowBanModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-md p-6 w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Ban {selectedDriver?.name}</h2>
              <button
                onClick={() => setShowBanModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
                aria-label="Close ban modal"
              >
                &times;
              </button>
            </div>

            <textarea
              rows={4}
              placeholder="Enter ban reason"
              value={banReason}
              onChange={e => setBanReason(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowBanModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleBanConfirm}
                disabled={!isAdmin}
                title={isAdmin ? 'Confirm Ban' : 'Admins only'}
                className={
                  'px-4 py-2 rounded ' +
                  (isAdmin
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                }
              >
                Confirm Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Modal — standalone */}
      {showDetails && detailsDriver && (
        <DriverDetailsModal driver={detailsDriver} onClose={closeDetails} />
      )}
    </>
  );
}
